<template>
    <div class="container">
      <h2 class="title">Grades Management</h2>
  
      <div class="filter-bar">
        <input v-model="search" class="form-input" placeholder="Search by student name..." />
        <button class="btn add-btn" @click="openAddModal">Add Grade</button>
      </div>
  
      <div class="table-container" v-if="grades.length > 0">
        <table class="modern-table">
          <thead>
            <tr>
              <th>Student</th>
              <th>Subject</th>
              <th>Term</th>
              <th>Score</th>
              <th>Teacher</th>
              <th>Exam</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="grade in filteredGrades" :key="grade.id">
              <td>{{ grade.student?.name }}</td>
              <td>{{ grade.subject?.name }}</td>
              <td>{{ grade.term?.name }}</td>
              <td>{{ grade.score }}</td>
              <td>{{ grade.teacher?.name }}</td>
              <td>{{ grade.exam?.title || '—' }}</td>
              <td>
                <button class="btn edit-btn" @click="editGrade(grade)">Edit</button>
                <button class="btn delete-btn" @click="deleteGrade(grade.id)">Delete</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
  
      <div v-else class="no-results">No grades found.</div>
  
      <!-- Add/Edit Grade Modal -->
      <div v-if="showModal" class="modal-overlay">
        <div class="modal-content">
          <div class="modal-header">
            <h3>{{ isEditing ? 'Edit Grade' : 'Add New Grade' }}</h3>
            <button class="close-btn" @click="closeModal">&times;</button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="submitGrade">
<!-- Class -->
<div class="form-group">
  <label>Class</label>
  <select v-model="form.class_id" @change="fetchSections" required>
    <option value="" disabled selected>Select Class</option>
    <option v-for="c in classes" :key="c.id" :value="c.id">
      {{ c.name }}
    </option>
  </select>
</div>

<!-- Section -->
<div class="form-group" v-if="sections.length">
  <label>Section</label>
  <select v-model="form.section_id" @change="fetchStudents" required>
    <option value="" disabled>Select Section</option>
    <option v-for="s in sections" :key="s.id" :value="s.id">{{ s.name }}</option>
  </select>
</div>

<!-- Student (filtered after section is picked) -->
<!-- Student (filtered after section is picked) -->
<div class="form-group" v-if="form.section_id">
  <label>Student</label>
  <select v-model="form.student_id" required>
    <option value="" disabled>Select Student</option>
    <option 
      v-for="student in students" 
      :key="student.id" 
      :value="student.id"
    >
      {{ student.name }}
    </option>
  </select>
  <p v-if="students.length === 0" class="text-sm text-gray-400">
    No students found for this section
  </p>
</div>

  
              <div class="form-group">
                <label>Subject</label>
                <select v-model="form.subject_id" required>
                  <option value="" disabled>Select Subject</option>
                  <option v-for="subject in subjects" :key="subject.id" :value="subject.id">
                    {{ subject.name }}
                  </option>
                </select>
              </div>
  
              <div class="form-group">
                <label>Term</label>
                <select v-model="form.term_id" required>
                  <option value="" disabled>Select Term</option>
                  <option v-for="term in terms" :key="term.id" :value="term.id">
                    {{ term.name }}
                  </option>
                </select>
              </div>
  
              <div class="form-group">
                <label>Teacher</label>
                <select v-model="form.teacher_id" required>
                  <option value="" disabled>Select Teacher</option>
                  <option v-for="teacher in teachers" :key="teacher.id" :value="teacher.id">
                    {{ teacher.name }}
                  </option>
                </select>
              </div>
  
              <div class="form-group">
                <label>Exam (Optional)</label>
                <select v-model="form.exam_id">
                  <option value="">Select Exam (Optional)</option>
                  <option v-for="exam in exams" :key="exam.id" :value="exam.id">
                    {{ exam.name }}
                  </option>
                </select>
              </div>
  
              <div class="form-group">
                <label>Score</label>
                <input type="number" v-model="form.score" min="0" max="100" step="0.01" required />
              </div>
  
              <div class="form-group">
                <label>Remarks (Optional)</label>
                <textarea v-model="form.remarks" rows="2"></textarea>
              </div>
  
              <div class="form-actions">
                <button type="button" class="btn cancel-btn" @click="closeModal">Cancel</button>
                <button type="submit" class="btn submit-btn">
                  {{ isEditing ? 'Update' : 'Save' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  
  const api = axios.create({
    baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api',
  });
  
  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  }, (error) => Promise.reject(error));
  
  export default {
    data() {
      return {
        grades: [],
        students: [],
        subjects: [],
        terms: [],
        teachers: [],
        classes:[],
sections: [],

        exams: [],
        search: '',
        isLoading: false,
        showModal: false,
        isEditing: false,
        currentGradeId: null,
        form: {
  class_id: '',
  section_id: '',
  student_id: '',
  subject_id: '',
  term_id: '',
  teacher_id: '',
  exam_id: '',
  score: '',
  remarks: ''
}

      };
    },
    computed: {
      filteredGrades() {
        return this.grades.filter((grade) =>
          grade.student?.name?.toLowerCase().includes(this.search.toLowerCase())
        );
      },
    },
    async mounted() {
      await this.fetchGrades();
      await this.fetchDropdownData();

    },
    methods: {
        async fetchSections() {
  this.sections = [];
  this.students = [];
  this.form.section_id = '';
  this.form.student_id = '';

  if (!this.form.class_id) return;

  try {
    const res = await api.get(`/classes/${this.form.class_id}/sections`);
    this.sections = res.data?.sections || res.data?.data || res.data || [];
    console.log('Fetched sections:', this.sections); // Debug log
  } catch (err) {
    console.error('Failed to fetch sections:', err);
    this.$toast?.error('Failed to load sections');
    this.sections = []; // Ensure sections is always an array
  }
},

async fetchStudents() {
  this.students = [];
  this.form.student_id = '';

  if (!this.form.class_id || !this.form.section_id) return;

  try {
    const res = await api.get(`/students?class_id=${this.form.class_id}&section_id=${this.form.section_id}`);
    // Make sure we're getting the data in the expected format
    this.students = res.data?.students || res.data || [];
    console.log('Fetched students:', this.students); // Debug log
  } catch (err) {
    console.error('Failed to fetch students:', err);
    this.$toast?.error('Failed to load students');
    this.students = []; // Ensure students is always an array
  }
},
      async fetchGrades() {
        this.isLoading = true;
        try {
          const res = await api.get('/grades');
          this.grades = res.data?.data ?? [];
        } catch (error) {
          console.error('Error fetching grades:', error);
          this.$toast?.error('Failed to load grades');
        } finally {
          this.isLoading = false;
        }
      },
      async fetchDropdownData() {
  try {
    const [classesRes, subjectsRes, termsRes, teachersRes, examsRes] = await Promise.all([
      api.get('/classes'),
      api.get('/subjects'),
      api.get('/terms'),
      api.get('/users?role=teacher'),
      api.get('/exams')
    ]);
    
    // Handle different response structures
    this.classes = classesRes.data || classesRes.data || [];
    this.subjects = subjectsRes.data?.data || subjectsRes.data || [];
    this.terms = termsRes.data?.data || termsRes.data || [];
    this.teachers = teachersRes.data?.data || teachersRes.data || [];
    this.exams = examsRes.data?.data || examsRes.data || [];
    
    console.log('Dropdown data loaded:', {
      classes: this.classes,
      subjects: this.subjects,
      terms: this.terms,
      teachers: this.teachers,
      exams: this.exams
    });
  } catch (error) {
    console.error('Error fetching dropdown data:', error);
    this.$toast?.error('Failed to load dropdown data');
  }
},
      openAddModal() {
        this.isEditing = false;
        this.currentGradeId = null;
        this.resetForm();
        this.showModal = true;
      },
      editGrade(grade) {
  this.isEditing = true;
  this.currentGradeId = grade.id;
  this.resetForm();

  // Set the form data
  this.form = {
    student_id: grade.student_id,
    subject_id: grade.subject_id,
    term_id: grade.term_id,
    teacher_id: grade.teacher_id,
    exam_id: grade.exam_id || '',
    score: grade.score,
    remarks: grade.remarks || ''
  };

  // If the student has class/section info, fetch it
  if (grade.student?.class_id) {
    this.form.class_id = grade.student.class_id;
    this.fetchSections().then(() => {
      if (grade.student?.section_id) {
        this.form.section_id = grade.student.section_id;
        this.fetchStudents();
      }
    });
  }

  this.showModal = true;
},
      closeModal() {
        this.showModal = false;
      },
      resetForm() {
        this.form = {
            class_id: '',
            section_id: '',
          student_id: '',
          subject_id: '',
          term_id: '',
          teacher_id: '',
          exam_id: '',
          score: '',
          remarks: ''
        };
      },
      async submitGrade() {
  try {
    // Prepare the data to send (excluding class_id and section_id)
    const gradeData = {
      student_id: this.form.student_id,
      subject_id: this.form.subject_id,
      term_id: this.form.term_id,
      teacher_id: this.form.teacher_id,
      exam_id: this.form.exam_id || null,
      score: this.form.score,
      remarks: this.form.remarks || null
    };

    if (this.isEditing) {
      await api.put(`/grades/${this.currentGradeId}`, gradeData);
      this.$toast?.success('Grade updated successfully');
    } else {
      await api.post('/grades', gradeData);
      this.$toast?.success('Grade added successfully');
    }
    await this.fetchGrades();
    this.closeModal();
  } catch (error) {
    console.error('Error saving grade:', error);
    this.$toast?.error(error.response?.data?.message || 'Failed to save grade');
  }
},
      async deleteGrade(id) {
        if (!confirm('Are you sure you want to delete this grade?')) return;
        try {
          await api.delete(`/grades/${id}`);
          this.grades = this.grades.filter((g) => g.id !== id);
          this.$toast?.success('Grade deleted');
        } catch (err) {
          console.error('Delete failed:', err);
          this.$toast?.error('Could not delete grade');
        }
      },
    },
    watch: {
  'form.class_id'(newVal) {
    if (newVal) {
      this.fetchSections();
    } else {
      this.sections = [];
      this.form.section_id = '';
      this.students = [];
      this.form.student_id = '';
    }
  },
  'form.section_id'(newVal) {
    if (newVal && this.form.class_id) {
      this.fetchStudents();
    } else {
      this.students = [];
      this.form.student_id = '';
    }
  }
}

  };
  </script>
  
  <style scoped>
  .container {
    padding: 2rem;
    background: #0a192f;
    color: #e6f1ff;
  }
  
  .title {
    font-size: 1.8rem;
    color: #64ffda;
    margin-bottom: 1rem;
  }
  
  .filter-bar {
    margin-bottom: 1rem;
    display: flex;
    gap: 1rem;
  }
  
  .form-input {
    padding: 0.5rem 1rem;
    border-radius: 6px;
    background: #112240;
    color: #fff;
    border: 1px solid #1e2a47;
    flex: 1;
    max-width: 300px;
  }
  
  .table-container {
    overflow-x: auto;
  }
  
  .modern-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 1rem;
  }
  
  .modern-table th,
  .modern-table td {
    padding: 0.75rem;
    border-bottom: 1px solid #1e2a47;
  }
  
  .btn {
    margin: 0 5px;
    padding: 6px 12px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
  }
  
  .add-btn {
    background: #3b82f6;
    color: white;
  }
  
  .edit-btn {
    background: #2563eb;
    color: white;
  }
  
  .delete-btn {
    background: #dc2626;
    color: white;
  }
  
  .no-results {
    margin-top: 2rem;
    color: #8892b0;
  }
  
  /* Modal Styles */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }
  
  .modal-content {
    background: #112240;
    border-radius: 8px;
    width: 90%;
    max-width: 600px;
    max-height: 90vh;
    overflow-y: auto;
    padding: 1.5rem;
  }
  
  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
  }
  
  .modal-header h3 {
    color: #64ffda;
    margin: 0;
  }
  
  .close-btn {
    background: none;
    border: none;
    color: #8892b0;
    font-size: 1.5rem;
    cursor: pointer;
  }
  
  .form-group {
    margin-bottom: 1rem;
  }
  
  .form-group label {
    display: block;
    margin-bottom: 0.5rem;
    color: #ccd6f6;
  }
  
  
  .form-group select,
  .form-group input,
  .form-group textarea {
    width: 100%;
    padding: 0.5rem;
    border-radius: 4px;
    background: #0a192f;
    border: 1px solid #1e2a47;
    color: #e6f1ff;
  }

  .form-group select {
  display: block;
  width: 100%;
  padding: 0.5rem;
  margin-top: 0.25rem;
  background: #112240;
  color: #e6f1ff;
  border: 1px solid #1e2a47;
  border-radius: 4px;
  appearance: none; /* Remove default styling */
}
  
  .form-group textarea {
    resize: vertical;
  }
  
  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 1.5rem;
  }
  
  .cancel-btn {
    background: #64748b;
    color: white;
  }
  
  .submit-btn {
    background: #3b82f6;
    color: white;
  }
  </style>